import React, { useCallback, useState } from 'react'
import clsx from 'classnames'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router'
import { YMaps } from '@pbe/react-yandex-maps'
import { InView } from 'react-intersection-observer'
import { getSelectedCity } from 'src/store/data.store'
import { setAppointmentSalon } from 'src/store/appointment.store'
import { SalonsList } from './SalonsList'
import { SalonsMap } from './SalonsMap'
import { SalonsModePicker, type SalonsMode } from './SalonsModePicker'
import styles from './Salons.module.scss'

type SalonsProps = {
  listPageSize?: number,
  lazy?: boolean,
  withCityTitle?: boolean,
  /** Without it, choosing a salon opens booking with that salon */
  onSelectSalon?: (salonId: number) => void,
}

/** Salons of the selected city on a map or as a list */
export const Salons: React.FC<SalonsProps> = React.memo((props) => {
  const { listPageSize = 8, lazy = true, withCityTitle = false, onSelectSalon } = props
  const [mode, setMode] = useState<SalonsMode>('map')
  const city = useSelector(getSelectedCity)
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const toggleMode = useCallback(() => {
    setMode((current) => (current === 'list' ? 'map' : 'list'))
  }, [])

  const handleSelectSalon = useCallback((salonId: number) => {
    dispatch(setAppointmentSalon(salonId))
    if (onSelectSalon) {
      onSelectSalon(salonId)
    } else {
      navigate('#appointment')
    }
  }, [onSelectSalon, dispatch, navigate])

  return (
    <div className={styles.container}>
      <div className={clsx([styles.yandexHeader, { [styles.withHeader]: withCityTitle }])}>
        {withCityTitle &&
          <div className={styles.cityTitleContainer}>
            <span className={styles.cityTitle}>Салоны в г. {city?.city?.name ?? ''}</span>
          </div>
        }
        <div className={styles.modePicker}>
          <SalonsModePicker mode={mode} onToggle={toggleMode} />
        </div>
      </div>
      <div className={styles.yandexMapContainer}>
        {mode === 'list'
          ? <SalonsList onSelectSalon={handleSelectSalon} perPage={listPageSize} />
          : (
            <InView>
              {({ ref, inView }) => (
                <div ref={ref} style={{ width: '100%' }}>
                  {inView && (
                    <YMaps version={'2.1'}>
                      <SalonsMap onSelectSalon={handleSelectSalon} lazy={lazy} city={city} />
                    </YMaps>
                  )}
                </div>
              )}
            </InView>
          )}
      </div>
    </div>
  )
})
Salons.displayName = 'Salons'
